import { createApp, reactive } from 'vue'
import App from './App.vue'
import GlobalFunctions from './utils/globalFunctions';
import loaderMessages from './utils/loaderMessages';
import deleteModalText from './utils/deleteModalText';
import store from './store/store.js'; 
import router from './router'
import axios from 'axios'
import VueApexCharts from 'vue3-apexcharts'

import mitt from 'mitt'
const emitter = mitt()
export default emitter;

// Device settings: the values below are the DEFAULTS. The gear panel (App Options) can change
// them for one browser only; that choice is saved in the browser and wins over the default.
const deviceSettingDefaults = {}; // the main.js values, used by "Reset to defaults" (helper: deviceSetting() at the bottom)

let soundsEnabled = deviceSetting('soundsEnabled', false);
let showSampleButton = deviceSetting('showSampleButton', false);
// true  = search runs only when the Search button (or Enter) is pressed
// false = search runs by itself when the user stops typing
let searchWithButtonClick = deviceSetting('searchWithButtonClick', true);
// Colour of the phone's top bar (status bar). Change it here.
let statusBarColor = "#0d6efd";
const notificationRefreshInterval = 10000; // or 5000 5 seconds
const recordsPerPage = deviceSetting('recordsPerPage', 5);
// true  = the app opens without internet (offline copy stored on the device)
// false = online only, as before (any offline copy on a device is removed)
// Rebuild (npm run build) after changing it.
const offlineEnabled = true;
// true = page messages look like phone toasts (rounded, shadow, icon). false = the normal Bootstrap alert
const toastAlerts = deviceSetting('toastAlerts', true);
// Look of every form box (FormField, SelectField, TagField):
//   'icons'    = label above, icon box in front
//   'plain'    = label above, no icon box
//   'floating' = label inside the box; it moves up onto the border on focus or when filled
const formStyle = deviceSetting('formStyle', 'icons');
//You can change also loader messages

//Form tooltips: 
// 'invalid' = only when a box is wrong, 
// 'focusAndInvalid' = also when you click into a box
const formTooltipMode = deviceSetting('formTooltipMode', 'invalid');

// Where alerts show: 'top' = top of the page / window (the window scrolls up to it)
//                    'bottom' = bottom of the screen, floating (nothing scrolls)
// Alerts default: top on computers (mouse), bottom on phones and tablets (touch).
// A choice saved in App Options still wins.
const isDesktopDevice = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
const alertPosition = deviceSetting('alertPosition', isDesktopDevice ? 'top' : 'bottom');

// true = when a form opens on a computer (mouse), the cursor goes into its first box.
// Phones and tablets never do this (the keyboard would cover the form). Default: off
const focusFirstFieldOnDesktop = deviceSetting('focusFirstFieldOnDesktop', false);

// Set the base URL for axios
// The API address follows where the app is opened: your computer = XAMPP, anywhere else = the live server
const isLocalApp = ['localhost', '127.0.0.1'].includes(window.location.hostname);
const linkUrl = isLocalApp
   ? 'http://127.0.0.1/smartadmin-custom-usermanager-php-backend'            // Localhost Folder
   : 'https://erpapi.wihfocus.com/smartadmin-custom-usermanager-php-backend'; // Cpanel Folder


axios.defaults.baseURL = linkUrl



// Create Vue apps
const app = createApp(App)
app.use(store); // Register the store with the app
app.use(GlobalFunctions); // Register the global functions with the app
app.use(VueApexCharts);

// Phone top bar colour: set once when the app starts
app.config.globalProperties.$statusBarColor = statusBarColor;
app.config.globalProperties.$setStatusBarColor(statusBarColor);

// Set global property for base URL
app.config.globalProperties.$baseUrl = linkUrl
app.config.globalProperties.$notificationRefreshInterval = notificationRefreshInterval;
app.config.globalProperties.$loaderMessages = loaderMessages;
app.config.globalProperties.$deleteModalText = deleteModalText;
app.config.globalProperties.$offlineEnabled = offlineEnabled;

// Device settings (gear panel → App Options): watched values, so a change shows at once (no reload).
// Pages read them as before: this.$formStyle, this.$toastAlerts, this.$soundsEnabled ...
const deviceSettings = reactive({
   soundsEnabled,
   showSampleButton,
   searchWithButtonClick,
   toastAlerts,
   formStyle,
   formTooltipMode,
   alertPosition,
   recordsPerPage,
   focusFirstFieldOnDesktop,
});
Object.keys(deviceSettings).forEach((key) => {
   Object.defineProperty(app.config.globalProperties, '$' + key, {
      get: () => deviceSettings[key],
      configurable: true,
   });
});
app.config.globalProperties.$deviceSettings = deviceSettings;
window.formTooltipMode = deviceSettings.formTooltipMode; // read by the tooltip code in initAdminScripts

// Save one device setting for this browser and apply it straight away
app.config.globalProperties.$saveDeviceSetting = function (key, value) {
   deviceSettings[key] = value;
   localStorage.setItem('setting_' + key, JSON.stringify(value));
   if (key === 'formTooltipMode') {
      window.formTooltipMode = value;
   }
};

// Forget this browser's choices: back to the main.js values
app.config.globalProperties.$resetDeviceSettings = function () {
   Object.keys(deviceSettingDefaults).forEach((key) => {
      localStorage.removeItem('setting_' + key);
      deviceSettings[key] = deviceSettingDefaults[key];
   });
   window.formTooltipMode = deviceSettings.formTooltipMode;
};



app.use(router).mount('#app')

// Offline on (or cleaned up when off): globalFunctions.js
app.config.globalProperties.$setupOffline(offlineEnabled);


//createApp(App).use(router).mount('#app')


// Device settings helper (used by the settings at the top): this browser's saved choice, or the main.js default.
// Written as a "function" so it can be used above this line.
function deviceSetting(key, defaultValue) {
   deviceSettingDefaults[key] = defaultValue;
   try {
      const saved = localStorage.getItem('setting_' + key);
      return saved === null ? defaultValue : JSON.parse(saved);
   } catch (e) {
      return defaultValue;
   }
}


